import CopyToClipboard from 'react-copy-to-clipboard';
import { Typography, TypographyTypeMap } from '@mui/material';
import { ReactElement, useCallback } from 'react';
import { Copy } from '@ankr.com/ui';

import { useStyles } from './CopyToClipIconStyles';
import { useCopyToClip } from './CopyToClipIconUtils';

export interface ICopyToClipIconProps {
  className?: string;
  copyText?: string;
  copyTitle?: string;
  hideIcon?: boolean;
  isDisabled?: boolean;
  message: string;
  onClick?: () => void;
  onCopy?: (text: string) => void;
  size?: 'm' | 'l';
  text: string;
  textLabel?: string | ReactElement;
  textClassName?: string;
  contentClassName?: string;
  messageClassName?: string;
  textColor?: TypographyTypeMap['props']['color'];
  isOnlyIconClickable?: boolean;
}

export const CopyToClipIcon = ({
  className,
  contentClassName,
  copyText,
  copyTitle,
  hideIcon,
  isDisabled,
  isOnlyIconClickable,
  message,
  messageClassName,
  onClick,
  onCopy = () => {},
  size = 'm',
  text,
  textClassName,
  textColor = 'textSecondary',
  textLabel,
}: ICopyToClipIconProps) => {
  const [isCopied, setIsCopied] = useCopyToClip();
  const { classes, cx } = useStyles({ size, isDisabled });

  const handleCopy = useCallback(() => {
    if (isDisabled) {
      return;
    }

    if (onClick) {
      onClick();

      return;
    }

    setIsCopied();

    onCopy(text);
  }, [isDisabled, onClick, onCopy, setIsCopied, text]);

  const renderCopyIcon = useCallback(
    () => <Copy className={classes.copyIcon} />,
    [classes.copyIcon],
  );

  const renderCopyText = useCallback(
    () =>
      copyText ? (
        <Typography variant="body4" noWrap className={classes.copyText}>
          {copyTitle || copyText}
        </Typography>
      ) : null,
    [classes.copyText, copyText, copyTitle],
  );

  const renderTextLabel = useCallback(
    () => (
      <Typography
        variant="body4"
        noWrap
        className={cx(classes.text, textClassName)}
        color={textColor}
      >
        {textLabel || text}
      </Typography>
    ),
    [classes.text, cx, text, textClassName, textColor, textLabel],
  );

  const renderContent = useCallback(() => {
    if (isOnlyIconClickable && !hideIcon) {
      return (
        <div className={cx(classes.content, contentClassName)}>
          {renderTextLabel()}
          <div className={classes.copy}>
            {!hideIcon && (
              <CopyToClipboard text={text} onCopy={handleCopy}>
                {renderCopyIcon()}
              </CopyToClipboard>
            )}
            {renderCopyText()}
          </div>
        </div>
      );
    }

    return (
      <CopyToClipboard text={text} onCopy={handleCopy}>
        <div className={cx(classes.content, contentClassName)}>
          {renderTextLabel()}
          <div className={classes.copy}>
            {!hideIcon && renderCopyIcon()}
            {renderCopyText()}
          </div>
        </div>
      </CopyToClipboard>
    );
  }, [
    isOnlyIconClickable,
    cx,
    classes.content,
    classes.copy,
    contentClassName,
    renderTextLabel,
    renderCopyIcon,
    renderCopyText,
    hideIcon,
    text,
    handleCopy,
  ]);

  return (
    <div
      role="button"
      tabIndex={0}
      /* stop propagation for click event to avoid parent element click */
      onClick={event => {
        event.stopPropagation();
        event.preventDefault();
      }}
      className={cx(classes.container, className)}
    >
      {isCopied ? (
        <Typography
          variant="body4"
          className={cx(classes.message, messageClassName)}
        >
          {message}
        </Typography>
      ) : (
        renderContent()
      )}
    </div>
  );
};
